import PropTypes from "prop-types";
import React, { useMemo } from "react";

import { useDeviceInfo, XLARGE } from "@swa-ui/browser";
import { Heading, Layout, ListScroller, Shimmer } from "@swa-ui/core";
import { useFeaturedDealsAnalytics, useVacationPackagesAnalytics } from "@swa-ui/dotcom-analytics";
import i18n from "@swa-ui/locale";
import { CallToAction, TextContent, usePlacement } from "@swa-ui/placements";
import { classNames } from "@swa-ui/string";

import { PackageCard } from "./PackageCard";
import { useVacationDeals } from "./useVacationDeals";
import styles from "./VacationsPackagesPlacement.module.scss";

export const VacationsPackagesPlacement = (props) => {
  const { placement, placementData = {} } = props;
  const { callToAction: sectionCTA, carouselContent, headingAlignment, primaryHeading } = placement;
  const { analyticsValue = "" } = placementData;
  const { loading: isLoading, orderedResults: packagesDataList } =
    useVacationDeals(carouselContent);
  const { screenSize } = useDeviceInfo();
  const { setPlacementsAnalytics } = usePlacement();
  const memoizedItems = useMemo(() => getListScrollerItems(), [packagesDataList]);

  useFeaturedDealsAnalytics(packagesDataList, primaryHeading?.value);
  useVacationPackagesAnalytics(packagesDataList);

  return (
    (packagesDataList.length > 0 || isLoading) && (
      <>
        <Layout {...getTopLayoutProps()}>
          <Heading {...getHeadingProps()} />
          {renderSecondaryHeading()}
        </Layout>
        {isLoading ? renderLoading() : <ListScroller {...getListScrollerProps()} />}
        {renderCallToAction()}
      </>
    )
  );

  function renderLoading() {
    return (
      <Layout center={screenSize === XLARGE} sides>
        <Shimmer />
      </Layout>
    );
  }

  function renderSecondaryHeading() {
    const { secondaryHeading } = placement;
    let node = null;

    if (secondaryHeading?.value) {
      node = (
        <div className={getHeadingClass()} style={{ ...secondaryHeading }}>
          {secondaryHeading.value}
        </div>
      );
    }

    return node;
  }

  function renderCallToAction() {
    return (
      sectionCTA && (
        <Layout {...getBottomLayoutProps()}>
          <CallToAction {...sectionCTA} />
        </Layout>
      )
    );
  }

  function getTopLayoutProps() {
    return {
      bottom: "medium",
      center: true,
      sides: true,
    };
  }

  function getHeadingProps() {
    return {
      children: <TextContent {...(primaryHeading || {})} />,
      className: getHeadingClass(),
      headingLevel: 2,
    };
  }

  function getHeadingClass() {
    return classNames({
      [styles.headingCenter]: headingAlignment === "center",
    });
  }

  function getListScrollerProps() {
    return {
      "aria-label-current-page": i18n("PackagesPlacement__ARIA_LABEL__CURRENT_PAGE"),
      "aria-label-next": i18n("PackagesPlacement__ARIA_LABEL__NEXT"),
      "aria-label-page-count": i18n("PackagesPlacement__ARIA_LABEL__PAGE_COUNT"),
      "aria-label-previous": i18n("PackagesPlacement__ARIA_LABEL__PREVIOUS"),
      className: styles.scroller,
      center: packagesDataList.length < 4,
      items: memoizedItems,
      layout: "none",
      onClick: handleListScrollerClick,
    };
  }

  function getBottomLayoutProps() {
    return {
      center: true,
      className: styles.callToAction,
      sides: true,
      top: "medium",
    };
  }

  function getListScrollerItems() {
    return packagesDataList.map((item) => (
      <PackageCard
        callToAction={carouselContent.callToAction}
        key={item.resultSetId}
        navigationLink={item.navigationLink}
        packageData={item.packageData}
      />
    ));
  }

  function handleListScrollerClick() {
    if (analyticsValue) {
      setPlacementsAnalytics({
        eventType: "scroll",
        eventDescription: analyticsValue,
      });
    }
  }
};

VacationsPackagesPlacement.propTypes = {
  /** Placement object for packages section. */
  placement: PropTypes.shape({
    callToAction: PropTypes.object,
    carouselContent: PropTypes.shape({
      callToAction: PropTypes.object,
      packageIds: PropTypes.arrayOf(
        PropTypes.shape({
          tripProfileId: PropTypes.string,
          searchProfileId: PropTypes.string,
        })
      ),
    }),
    headingAlignment: PropTypes.oneOf(["left", "center"]),
    primaryHeading: PropTypes.object,
    secondaryHeading: PropTypes.object,
  }),
  placementData: PropTypes.shape({
    /** The dynamic value that will be used as an event description for analytics. */
    analyticsValue: PropTypes.string,
  }),
};
